/**
 * Redeem Service
 *
 * A single public, unauthenticated call. Deliberately not a BaseAPIClient subclass:
 * those all take a token, and this call has none — redeeming a code is what creates
 * the guest's token in the first place.
 *
 * A Clerk token is passed only when the caller happens to be signed in, so the
 * backend can reject the redemption before it burns the code (see the redeem
 * handler in routers/accessCodes.ts). Without it the API is a separate origin with
 * no credentialed CORS, so the server would see no session at all.
 */

export interface RedeemAccessCodeResponse {
  sessionToken: string;
}

export async function redeemAccessCode(
  code: string,
  clerkToken?: string | null,
): Promise<RedeemAccessCodeResponse> {
  const baseUrl = import.meta.env.VITE_SERVER_URL || '';

  let response: Response;
  try {
    response = await fetch(`${baseUrl}/access-codes/redeem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(clerkToken ? { Authorization: `Bearer ${clerkToken}` } : {}),
      },
      body: JSON.stringify({ code }),
    });
  } catch {
    // fetch itself throws on a network-level failure (offline, DNS, CORS) —
    // distinct from the HTTP-status handling below, and worth a message a
    // candidate can actually act on rather than a raw "Failed to fetch".
    throw new Error("Couldn't reach the server — check your connection and try again.");
  }

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body && typeof body.message === 'string') message = body.message;
    } catch {
      // Non-JSON error body — keep the status-line fallback above.
    }
    throw new Error(message);
  }

  return response.json();
}
